import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Copy, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertTriangle,
  User,
  Mail,
  Building,
  Shield,
  Clock,
  Key
} from "lucide-react";
import { toast } from "sonner";
import { generateSecurePassword, copyToClipboard, GeneratedPassword } from "@/utils/passwordGenerator";
import { supabase } from "@/integrations/supabase/client";
import { addDays, format } from "date-fns";
import { PasswordResetDialog } from './PasswordResetDialog';

interface TrialRequest {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  district: string;
  role: string;
  state: string;
  message: string;
  status: string;
  created_at: string;
}

interface TrialUserProvisioningProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: TrialRequest | null;
  trialDuration: string;
  onTrialDurationChange: (duration: string) => void;
  onProvisionComplete: () => void;
}

export function TrialUserProvisioning({
  open,
  onOpenChange,
  request,
  trialDuration,
  onTrialDurationChange,
  onProvisionComplete
}: TrialUserProvisioningProps) {
  const [generatedPassword, setGeneratedPassword] = useState<GeneratedPassword | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'generate' | 'provision' | 'complete'>('generate');
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);

  React.useEffect(() => {
    if (open && request) {
      // Auto-generate password when dialog opens
      handleGeneratePassword();
      setStep('generate');
    }
  }, [open, request]);

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(12);
    setGeneratedPassword(newPassword);
    setShowPassword(false);
  };

  const handleCopyPassword = async () => {
    if (!generatedPassword) return;
    
    const success = await copyToClipboard(generatedPassword.password);
    if (success) {
      toast.success('Password copied to clipboard!');
    } else {
      toast.error('Failed to copy password. Please copy it manually.');
    }
  };

  const provisionTrialUser = async () => {
    if (!request || !generatedPassword) return;

    try {
      setLoading(true);
      setStep('provision');

      // Call edge function to create trial user
      const { data, error } = await supabase.functions.invoke('create-trial-user', {
        body: {
          request,
          generatedPassword: generatedPassword.password,
          trialDuration
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      if (!data?.success) {
        console.error('Function returned error:', data);
        throw new Error(data?.error || 'Failed to create trial user');
      }

      const trialEndDate = new Date(data.trialEndDate);
      setStep('complete');
      toast.success(`Trial user created! Trial expires on ${format(trialEndDate, 'MMM dd, yyyy')}`);
      
    } catch (error) {
      console.error('Error provisioning trial user:', error);
      toast.error(`Failed to create trial user: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setStep('generate');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep('generate');
      setGeneratedPassword(null);
      setShowPassword(false);
    }, 300);
  };

  const handleComplete = () => {
    onProvisionComplete();
    handleClose();
  };

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Provision Trial User
          </DialogTitle>
          <DialogDescription>
            Create a trial account for {request.first_name} {request.last_name}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-6">
          <div className="space-y-6 pb-4">
            {/* User Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">User Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{request.first_name} {request.last_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{request.email}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{request.district}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{request.role}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {step === 'generate' && (
            <>
              <Separator />
              
              {/* Password Generation */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Temporary Password</h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleGeneratePassword}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Regenerate
                  </Button>
                </div>

                {generatedPassword && (
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Label htmlFor="generated-password">Generated Password</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                id="generated-password"
                                type={showPassword ? "text" : "password"}
                                value={generatedPassword.password}
                                readOnly
                                className="font-mono"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCopyPassword}
                                className="flex items-center gap-1"
                              >
                                <Copy className="h-4 w-4" />
                                Copy
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium">Strong Password</span>
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            12 characters
                          </Badge>
                        </div>

                        <Alert>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>Important:</strong> Copy this password now and share it securely with the user. 
                            They will be required to change it on their first login.
                          </AlertDescription>
                        </Alert>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <Separator />

              {/* Trial Duration */}
              <div className="space-y-3">
                <Label htmlFor="trial-duration" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Trial Duration (days)
                </Label>
                <Input
                  id="trial-duration"
                  type="number"
                  value={trialDuration}
                  onChange={(e) => onTrialDurationChange(e.target.value)}
                  min="1"
                  max="365"
                  className="w-32"
                />
                <p className="text-sm text-muted-foreground">
                  Trial will expire on {format(addDays(new Date(), parseInt(trialDuration)), 'MMM dd, yyyy')}
                </p>
              </div>
            </>
          )}

          {step === 'provision' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Creating trial account...</p>
            </div>
          )}

          {step === 'complete' && (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                  <div>
                    <h3 className="text-lg font-medium text-green-900">Trial Account Created!</h3>
                    <p className="text-green-700 mt-1">
                      {request.first_name} {request.last_name} can now log in with their email and the provided password.
                    </p>
                  </div>
                  <Alert className="bg-green-100 border-green-300">
                    <AlertTriangle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Make sure to securely share the password with the user. They must change it on first login.
                    </AlertDescription>
                  </Alert>
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPasswordResetOpen(true)}
                      className="flex items-center gap-2"
                    >
                      <Key className="h-4 w-4" />
                      Reset Password
                    </Button>
                    <p className="text-sm text-green-600 mt-2">
                      Need to reset the password? Use the button above.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        </ScrollArea>

        <div className="flex justify-end gap-3 pt-4 border-t">
          {step === 'generate' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={provisionTrialUser} 
                disabled={!generatedPassword || loading}
                className="flex items-center gap-2"
              >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>}
                Create Trial Account
              </Button>
            </>
          )}
          
          {step === 'complete' && (
            <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700">
              Done
            </Button>
          )}
        </div>
      </DialogContent>
      
      <PasswordResetDialog
        open={passwordResetOpen}
        onOpenChange={setPasswordResetOpen}
        userEmail={request?.email}
      />
    </Dialog>
  );
}